import React from "react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Check, Clock, Minus, ArrowRight } from "lucide-react";

const STAGE_STYLE = {
  COMPLETE: "bg-emerald-100 border-emerald-300 text-emerald-700",
  CURRENT: "bg-blue-100 border-blue-300 text-blue-700",
  PENDING: "bg-amber-100 border-amber-300 text-amber-700",
  NOT_APPLICABLE: "bg-gray-100 border-gray-200 text-gray-500",
  BLOCKED: "bg-red-100 border-red-300 text-red-700",
};
const STAGE_ICON = { COMPLETE: Check, CURRENT: ArrowRight, PENDING: Clock, NOT_APPLICABLE: Minus, BLOCKED: Minus };

function buildStages(review, row) {
  const hasSource = !!(review.blue_file || review.red_file);
  const hasOcr = !!(row?.extracted_clock_in || row?.extracted_clock_out);
  const hasCorrection = (row?.correction_ids || []).length > 0;
  const officialStatus = review.official_record_status;
  const approved = ["APPROVED", "LOCKED_FOR_PAYROLL"].includes(officialStatus);
  const locked = officialStatus === "LOCKED_FOR_PAYROLL";
  const payrollPublished = review.payroll_line_status === "PUBLISHED";

  return [
    {
      key: "source", label: "Source", status: hasSource ? "COMPLETE" : "PENDING",
      detail: hasSource
        ? [review.blue_file && `BLUE: ${review.blue_file.original_filename}`, review.red_file && `RED: ${review.red_file.original_filename}`].filter(Boolean).join(" / ")
        : "No scan registered yet for this month.",
    },
    {
      key: "ocr", label: "OCR", status: !hasSource ? "NOT_APPLICABLE" : hasOcr ? "COMPLETE" : "CURRENT",
      detail: hasOcr ? `Extracted: ${row.extracted_clock_in || "?"} - ${row.extracted_clock_out || "?"} (confidence ${row.extraction_confidence || "-"})` : "Not yet extracted.",
    },
    {
      key: "correction", label: "Correction", status: hasCorrection ? "COMPLETE" : "NOT_APPLICABLE",
      detail: hasCorrection ? `${row.correction_ids.length} correction(s) recorded for this day.` : "No manual correction was needed for this day.",
    },
    {
      key: "approved", label: "Approved", status: approved ? "COMPLETE" : officialStatus === "REOPENED" ? "PENDING" : hasOcr ? "CURRENT" : "NOT_APPLICABLE",
      detail: approved ? `Approved by ${review.approved_by || review.manager_approver || "-"} on ${(review.approved_at || "").slice(0, 10)}` : "Month not yet approved.",
    },
    {
      key: "payroll", label: "Payroll", status: payrollPublished ? "COMPLETE" : locked ? "PENDING" : "NOT_APPLICABLE",
      detail: payrollPublished ? `Payroll line ${review.payroll_line_id} is PUBLISHED - this record has been consumed.` : locked ? "Locked for payroll - awaiting a payroll run to consume it." : "Not yet locked for payroll.",
    },
  ];
}

/** Truthful per-record lineage trail: Source -> OCR -> Correction -> Approved -> Payroll.
 * Never marks Payroll COMPLETE unless a payroll_line for this employee/month actually exists
 * with status PUBLISHED - a LOCKED_FOR_PAYROLL record with no payroll line yet is PENDING, not
 * complete, so nobody mistakes "ready for payroll" for "already paid". */
export const LineageIndicator = ({ review, row, compact = false }) => {
  const stages = buildStages(review, row);
  return (
    <div className="flex items-center gap-0.5 flex-wrap" data-testid={`lineage-indicator${row ? `-${row.date}` : ""}`}>
      {stages.map((s, i) => {
        const Icon = STAGE_ICON[s.status];
        return (
          <React.Fragment key={s.key}>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  data-testid={`lineage-stage-${s.key}${row ? `-${row.date}` : ""}`}
                  className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-semibold ${STAGE_STYLE[s.status]}`}
                  title={s.label}
                >
                  <Icon size={9} /> {!compact && s.label}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-64 text-xs p-3 space-y-1" data-testid={`lineage-provenance-${s.key}`}>
                <p className="font-bold text-gray-700">{s.label} - {s.status.replace("_", " ")}</p>
                <p className="text-gray-500">{s.detail}</p>
              </PopoverContent>
            </Popover>
            {i < stages.length - 1 && <span className="text-gray-300 text-[10px]">-&gt;</span>}
          </React.Fragment>
        );
      })}
    </div>
  );
};
